// Action creator
import { MruItem } from "@ms/planner-models/lib/local/client/mru/MruItem";
import { Bucket } from "@ms/planner-models/lib/local/client/planner/bucket/Bucket";
import { ChatMessage } from "@ms/planner-models/lib/local/client/planner/chat/ChatMessage";
import { CustomFieldDefinition } from "@ms/planner-models/lib/local/client/planner/customFields/CustomFieldDefinition";
import { Dependency } from "@ms/planner-models/lib/local/client/planner/dependency/Dependency";
import { QueryRelationship, SingleEntityQueryType } from "@ms/planner-models/lib/local/client/planner/EntityRelationships";
import { EntityType } from "@ms/planner-models/lib/local/client/planner/EntityType";
import { Goal } from "@ms/planner-models/lib/local/client/planner/goal/Goal";
import { Plan } from "@ms/planner-models/lib/local/client/planner/plan/Plan";
import { Task } from "@ms/planner-models/lib/local/client/planner/task/Task";
import { TodoSettings } from "@ms/planner-models/lib/local/client/todo/TodoSettings/TodoSettings";
import { calculateChatDeltaLink } from "@ms/planner-models/lib/local/utilities/ChatDeltaLinkUtilities";
import { parseTrouterSubscriptionResponse } from "@ms/planner-models/lib/local/utilities/TrouterUtilities";
// Actions
import { FetchTodoSettingsSucceededAction } from "@ms/planner-actions/lib/local/appContext/FetchTodoSettingsAction";
import { FetchEntityCollectionSucceededAction, FetchEntitySucceededAction } from "@ms/planner-actions/lib/local/EntityActions";
import { FetchMruItemsSucceededAction } from "@ms/planner-actions/lib/local/mru/MruActions";
import { FetchTeamsTasksTabsSucceededAction } from "@ms/planner-actions/lib/local/teamsTasksTabs/FetchTeamsTasksTabsActions";
import { TrouterSubscriptionFailedAction, TrouterSubscriptionSuccessAction } from "@ms/planner-actions/lib/local/trouter/TrouterActions";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Utilities
import { getPlanRouteDetailsForTab } from "@ms/uno-routing/lib/local/utilities/TeamsTabParsingUtilities";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Constants
import { PlanType } from "@ms/uno-constants/lib/local/AppConstants";
import { PreFetchedDataKeys } from "@ms/uno-constants/lib/local/DataPreFetcherConstants";
/**
 * Action creator storing the pre-fetched data
 */ export class DataPreFetchActionCreator {
    storePrefetchedServiceResult(result) {
        if (result.errorMessage) {
            this.loggers.traceLogger.logTrace(0x1e150560 /* tag_4fqv6 */ , TraceLevel.Warning, `PreFetcher failed: [Type:${result.type}][Message:${result.errorMessage}]`);
            return;
        }
        switch(result.type){
            case PreFetchedDataKeys.Mru:
                this.storePreFetchedMruData(result.data);
                break;
            case PreFetchedDataKeys.MyDayTasks:
                this.storePreFetchedMyDayTasksData(result.data, result.nextLink, result.deltaLink, result.trouterViewUpdateKey);
                break;
            case PreFetchedDataKeys.ATMTasks:
                this.storePreFetchedATMTasksData(result.data, result.nextLink, result.deltaLink, result.trouterViewUpdateKey);
                break;
            case PreFetchedDataKeys.Plan:
                this.storePreFetchedPlanData(result.data, result.trouterViewUpdateKey);
                break;
            case PreFetchedDataKeys.TTTabs:
                this.storePreFetchedTTTabsData(result.data);
                break;
            case PreFetchedDataKeys.Buckets:
                this.storePreFetchedBucketsData(result.planId, result.data, result.nextLink, result.deltaLink, result.trouterViewUpdateKey);
                break;
            case PreFetchedDataKeys.BasicPlanTasks:
                this.storePreFetchedPlanTasksData(result.planId, result.data, result.nextLink, result.deltaLink, result.trouterViewUpdateKey);
                break;
            case PreFetchedDataKeys.TodoSettings:
                this.storePreFetchedTodoSettings(result.data);
                break;
            case PreFetchedDataKeys.CustomFieldDefinitions:
                this.storePreFetchedCustomFieldDefinitionsData(result.planId, result.data, result.deltaLink, result.trouterViewUpdateKey);
                break;
            case PreFetchedDataKeys.Dependencies:
                this.storePreFetchedDependenciesData(result.planId, result.data, result.nextLink, result.deltaLink, result.trouterViewUpdateKey);
                break;
            case PreFetchedDataKeys.Goals:
                this.storePreFetchedGoalsData(result.planId, result.data, result.deltaLink, result.trouterViewUpdateKey);
                break;
            case PreFetchedDataKeys.Task:
                if (result.data != null) {
                    this.storePreFetchedTaskData(result.data, result.trouterViewUpdateKey);
                }
                break;
            case PreFetchedDataKeys.TaskMessages:
                this.storePreFetchedTaskMessagesData(result.taskId, result.data, result.nextLink, result.trouterViewUpdateKey);
                break;
            default:
                this.loggers.traceLogger.logTrace(0x1e15055f /* tag_4fqv5 */ , TraceLevel.Warning, `Unsupported pre-fetched data type to save`);
                break;
        }
    }
    /**
     * Stores the pre-fetched MRU data
     * @param data: The pre-fetched MRU data
     */ storePreFetchedMruData(data) {
        const mruItems = [];
        data.map((item)=>MruItem.fromResource(item)).filter((mruItem)=>mruItem != null).forEach((mruItem)=>mruItems.push(mruItem));
        this.dispatcher.dispatchActionAsync(new FetchMruItemsSucceededAction(mruItems));
    }
    /**
     * Stores the pre-fetched MyDay tasks data
     * @param data: The pre-fetched MyDay tasks data
     * @param nextLink: The next link for pagination
     * @param deltaLink: The delta link for tracking changes
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ storePreFetchedMyDayTasksData(data, nextLink, deltaLink, trouterViewUpdateKey) {
        const userId = this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id;
        this.dispatchFetchedEntityCollection(this.accumulatedMyDayTasks, data, this.validatePlanIdAndReturnITask, EntityType.Task, EntityType.User, userId, QueryRelationship.TasksInMyDayOfUser, nextLink, deltaLink, trouterViewUpdateKey);
    }
    /**
     * Stores the pre-fetched ATM tasks data
     * @param data: The pre-fetched ATM tasks data
     * @param nextLink: The next link for pagination
     * @param deltaLink: The delta link for tracking changes
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ storePreFetchedATMTasksData(data, nextLink, deltaLink, trouterViewUpdateKey) {
        const userId = this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id;
        this.dispatchFetchedEntityCollection(this.accumulatedAtmTasks, data, this.validatePlanIdAndReturnITask, EntityType.Task, EntityType.User, userId, QueryRelationship.TasksAssignedToUser, nextLink, deltaLink, trouterViewUpdateKey);
    }
    /**
     * Stores the pre-fetched plan data
     * @param data: The pre-fetched plan data
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ storePreFetchedPlanData(data, trouterViewUpdateKey) {
        for (const plan of data){
            this.dispatcher.dispatchActionAsync(new FetchEntitySucceededAction(EntityType.Plan, Plan.fromResource(plan)));
            // Dispatch Trouter subscription result for each plan
            this.dispatchTrouterSubscriptionResult(trouterViewUpdateKey, plan.id, SingleEntityQueryType.PlanById);
        }
    }
    /**
     * Stores the pre-fetched TT tabs data
     * @param data: The pre-fetched TT tabs data
     */ storePreFetchedTTTabsData(data) {
        const tabs = data.map((tabResource)=>{
            const details = getPlanRouteDetailsForTab(tabResource.id, tabResource.tabContentUrl) ?? {
                planId: `invalidEntityId${tabResource.id}`,
                planType: PlanType.Basic
            };
            return {
                ...tabResource,
                tabName: decodeURIComponent(tabResource.tabName),
                entityId: details.planId,
                app: details.planType,
                orgId: details.planType === PlanType.Premium ? details.orgId : null
            };
        }) ?? [];
        this.dispatcher.dispatchActionAsync(new FetchTeamsTasksTabsSucceededAction(tabs));
    }
    /**
     * Stores the pre-fetched buckets data
     * @param planId: The plan id to which the buckets belong
     * @param data: The pre-fetched buckets data
     * @param nextLink: The next link for pagination
     * @param deltaLink: The delta link for tracking changes
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ storePreFetchedBucketsData(planId, data, nextLink, deltaLink, trouterViewUpdateKey) {
        this.dispatchFetchedEntityCollection(this.accumulatedBuckets, data, (resource)=>Bucket.fromResource(resource), EntityType.Bucket, EntityType.Plan, planId, QueryRelationship.BucketsInPlan, nextLink, deltaLink, trouterViewUpdateKey);
    }
    /**
     * Stores the pre-fetched tasks data for a basic plan
     * @param planId: The plan id to which the tasks belong
     * @param data: The pre-fetched tasks data
     * @param nextLink: The next link for pagination
     * @param deltaLink: The delta link for tracking changes
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ storePreFetchedPlanTasksData(planId, data, nextLink, deltaLink, trouterViewUpdateKey) {
        this.dispatchFetchedEntityCollection(this.accumulatedPlanTasks, data, (resource)=>Task.fromResource(resource), EntityType.Task, EntityType.Plan, planId, QueryRelationship.TasksInPlan, nextLink, deltaLink, trouterViewUpdateKey);
    }
    /**
     * Stores the pre-fetched dependencies data for a plan.
     * @param planId: The plan id to which the dependencies belong
     * @param data: The pre-fetched dependencies data
     * @param nextLink: The next link for pagination
     * @param deltaLink: The delta link for tracking changes
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ storePreFetchedDependenciesData(planId, data, nextLink, deltaLink, trouterViewUpdateKey) {
        this.dispatchFetchedEntityCollection(this.accumulatedDependencies, data, (resource)=>Dependency.fromResource(resource).withPlanId(planId), EntityType.Dependency, EntityType.Plan, planId, QueryRelationship.DependenciesInPlan, nextLink, deltaLink, trouterViewUpdateKey);
    }
    /**
     * Stores the pre-fetched goals data for a plan. Pre-fetch fetches only the first page;
     * the data fetch worker handles any subsequent pages.
     * @param planId: The plan id to which the goals belong
     * @param data: The pre-fetched goals data
     * @param deltaLink: The delta link for tracking changes
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ storePreFetchedGoalsData(planId, data, deltaLink, trouterViewUpdateKey) {
        this.dispatchFetchedEntityCollection([], data, (resource)=>Goal.fromResource(resource), EntityType.Goal, EntityType.Plan, planId, QueryRelationship.GoalsInPlan, undefined, deltaLink, trouterViewUpdateKey);
    }
    /**
     * Stores the pre-fetched custom field definitions data
     * @param planId: The plan id to which the custom field definitions belong
     * @param data: The pre-fetched custom field definitions data
     * @param deltaLink: The delta link for tracking changes
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ storePreFetchedCustomFieldDefinitionsData(planId, data, deltaLink, trouterViewUpdateKey) {
        this.dispatchFetchedEntityCollection([], data, (resource)=>CustomFieldDefinition.fromResource(resource), EntityType.CustomFieldDefinition, EntityType.Plan, planId, QueryRelationship.CustomFieldDefinitionsInPlan, undefined, deltaLink, trouterViewUpdateKey);
    }
    /**
     * Converts raw resources, accumulates entities into the provided array, dispatches a FetchEntityCollectionSucceededAction,
     * and dispatches the Trouter subscription result.
     * @param accumulator: The running accumulator array (mutated in place)
     * @param resourceData: The raw resource data to convert
     * @param fromResource: Converter function from resource to client entity
     * @param entityType: The type of the entities in the collection
     * @param parentEntityType: The type of the parent entity
     * @param parentEntityId: The ID of the parent entity
     * @param queryRelationship: The query relationship for this collection
     * @param nextLink: The next link for pagination
     * @param deltaLink: The delta link for tracking changes
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ dispatchFetchedEntityCollection(accumulator, resourceData, fromResource, entityType, parentEntityType, parentEntityId, queryRelationship, nextLink, deltaLink, trouterViewUpdateKey) {
        accumulator.push(...resourceData.map(fromResource));
        this.dispatcher.dispatchActionAsync(new FetchEntityCollectionSucceededAction(entityType, parentEntityType, parentEntityId, queryRelationship, accumulator, nextLink, true, accumulator.map((entity)=>entity.id), deltaLink));
        // Dispatch Trouter subscription result for the collection
        this.dispatchTrouterSubscriptionResult(trouterViewUpdateKey, parentEntityId, queryRelationship);
    }
    /**
     * Stores the pre-fetched single task data (deep-linked task)
     * @param data: The pre-fetched task resource
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ storePreFetchedTaskData(data, trouterViewUpdateKey) {
        const task = this.validatePlanIdAndReturnITask(data);
        this.dispatcher.dispatchActionAsync(new FetchEntitySucceededAction(EntityType.Task, task));
        this.dispatchTrouterSubscriptionResult(trouterViewUpdateKey, task.id, SingleEntityQueryType.TaskById);
    }
    /**
     * Stores the pre-fetched first page of chat messages for a task.
     *
     * The chat endpoint never returns a server-issued `@deltaLink`; we synthesize one from
     * `nextLink` + the oldest message's timestamp so delta sync is ready the moment prefetch
     * completes. `accumulatedEntityIds = []` because prefetch runs into an empty chat store and
     * the slice's loop adds each entity's unique ID to its prune allowlist — no baseline to
     * preserve. ChatModule's on-demand path takes over for any later pages.
     * @param taskId: The id of the task that owns these messages
     * @param data: The pre-fetched chat message resources for the first page
     * @param nextLink: The cursor returned by the messages endpoint (always present)
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ storePreFetchedTaskMessagesData(taskId, data, nextLink, trouterViewUpdateKey) {
        const messages = data.map((resource)=>ChatMessage.fromResource({
                ...resource,
                // Deleted messages may omit parentEntityId; fall back to the task we requested for.
                parentEntityId: resource.parentEntityId ?? taskId
            }));
        const deltaLink = calculateChatDeltaLink(nextLink, [], messages);
        this.dispatcher.dispatchActionAsync(new FetchEntityCollectionSucceededAction(EntityType.ChatMessage, EntityType.Task, taskId, QueryRelationship.ChatMessagesForTask, messages, nextLink, true, [], deltaLink ?? undefined));
        this.dispatchTrouterSubscriptionResult(trouterViewUpdateKey, taskId, QueryRelationship.ChatMessagesForTask);
    }
    /**
     * Stores the pre-fetched todo settings data
     * @param data: The pre-fetched settings data
     */ storePreFetchedTodoSettings(data) {
        const succeededAction = new FetchTodoSettingsSucceededAction(new TodoSettings(data));
        this.dispatcher.dispatchActionAsync(succeededAction);
    }
    /**
     * Dispatches the appropriate Trouter subscription action based on the response.
     * @param trouterViewUpdateKey The Trouter view update key from the response header
     * @param scopeEntityId The scope entity ID (parent for collections, entity itself for single)
     * @param queryType The query type (collection relationship or single entity type)
     */ dispatchTrouterSubscriptionResult(trouterViewUpdateKey, scopeEntityId, queryType) {
        const { EnableTrouterIntegration } = this.configProvider().flights;
        const { disable } = this.configProvider().settings.trouterSettings;
        if (!EnableTrouterIntegration || disable) {
            return;
        }
        const subscriptionResult = parseTrouterSubscriptionResponse(trouterViewUpdateKey);
        if (subscriptionResult.success) {
            this.dispatcher.dispatchActionAsync(new TrouterSubscriptionSuccessAction(subscriptionResult.viewUpdateKey, scopeEntityId, queryType));
        } else {
            this.dispatcher.dispatchActionAsync(new TrouterSubscriptionFailedAction(scopeEntityId, queryType, subscriptionResult.errorCode, subscriptionResult.shouldRetry, subscriptionResult.retryAfterSeconds));
        }
    }
    /**
     * Initializes a new instance of the DataPreFetchActionCreator.
     * @param storeLoader: Store loader
     * @param dispatcher: Dispatcher to dispatch actions
     * @param loggers: Logger for telemetry
     * @param configProvider: Config provider
     */ constructor(storeLoader, dispatcher, loggers, configProvider){
        this.storeLoader = storeLoader;
        this.dispatcher = dispatcher;
        this.loggers = loggers;
        this.configProvider = configProvider;
        // Accumulators to store entities across paginated responses
        this.accumulatedPlanTasks = [];
        this.accumulatedBuckets = [];
        this.accumulatedAtmTasks = [];
        this.accumulatedMyDayTasks = [];
        this.accumulatedDependencies = [];
        /**
     * Validates the plan ID of the given task resource and returns an ITask instance.
     * Logs a warning if the plan ID is missing.
     * @param resource The task resource to validate and convert
     * @returns The ITask instance created from the resource
     */ this.validatePlanIdAndReturnITask = (resource)=>{
            if (resource.planId == null) {
                this.loggers.traceLogger.logTrace(0x1d71b391 /* tag_321or */ , TraceLevel.Warning, `Task with missing planId`, {
                    assignmentCount: `${resource.userAssignments.length}`
                });
            }
            return Task.fromResource(resource);
        };
    }
}
